/**
 * Skupine nastavitev instance – iste kot v Supermanu (Administration): ključi na strežniku, oznake in vrsta vnosa.
 * Vsaka skupina se shrani posebej (samo njeni ključi, POST /api/nastavitve/posodobi); preverjanje in izpeljane
 * vrednosti (faktor DDV iz odstotkov, privzete vrednosti) naredi strežnik. Mape izvozov so samo v Swingu (mape na
 * računalniku): brskalnik prenose shrani v svojo mapo, zato v spletu nimajo učinka.
 */
export type VrstaPolja = 'besedilo' | 'dolgo' | 'da-ne' | 'stevilo';

export type PoljeNastavitve = {
  kljuc: string;
  oznaka: string;
  vrsta?: VrstaPolja;
  namig?: string;
  opis?: string;
};

/** {@code pomoc}: id članka pomoči (ikona »?« ob naslovu). */
export type SklopNastavitev = { naslov: string; polja: PoljeNastavitve[]; pomoc?: string };

/** {@code modul}: skupina se pokaže samo, če paket vključuje ta modul. */
export type SkupinaNastavitev = {
  kljuc: string;
  naziv: string;
  opis: string;
  sklopi: SklopNastavitev[];
  modul?: 'delavnica';
};

export const SKUPINE_NASTAVITEV: SkupinaNastavitev[] = [
  {
    kljuc: 'podjetje',
    naziv: 'Podjetje',
    opis: 'Podatki podjetja na dokumentih in za davčno potrjevanje.',
    sklopi: [
      {
        naslov: 'Podatki podjetja',
        polja: [
          { kljuc: 'narocnik', oznaka: 'Podjetje' },
          { kljuc: 'narocnikKratko', oznaka: 'Podjetje kratko' },
          { kljuc: 'narocnikKratko2', oznaka: 'Podjetje kratko 2' },
          { kljuc: 'narocnikDejavnostiPodjetja', oznaka: 'Glavna dejavnost' },
          { kljuc: 'narocnikIdDDV', oznaka: 'ID DDV', namig: 'SI12345678' },
          { kljuc: 'narocnikIdDDVNumber', oznaka: 'Davčna št.', namig: '12345678' },
          { kljuc: 'zavezanecZaDDV', oznaka: 'Zavezanec za DDV', vrsta: 'da-ne' },
          { kljuc: 'narocnikLei', oznaka: 'LEI' },
          { kljuc: 'narocnikMatStevilka', oznaka: 'Mat. številka' },
        ],
      },
    ],
  },
  {
    kljuc: 'naslov',
    naziv: 'Naslov in kontakt',
    opis: 'Naslov, kontakt in bančni podatki podjetja.',
    sklopi: [
      {
        naslov: 'Naslov',
        polja: [
          { kljuc: 'narocnikNaslovPodjetja', oznaka: 'Ulica' },
          { kljuc: 'narocnikHisnaStPodjetja', oznaka: 'Hišna št.' },
          { kljuc: 'narocnikPostnaStevilkaPodjetja', oznaka: 'Poštna št.' },
          { kljuc: 'narocnikMestoPodjetja', oznaka: 'Kraj' },
          { kljuc: 'narocnikDrzavaPodjetja', oznaka: 'Država', namig: 'Slovenija' },
          { kljuc: 'narocnikDrzavaKodaPodjetja', oznaka: 'Koda države', namig: 'SI' },
        ],
      },
      {
        naslov: 'Kontakt',
        polja: [
          { kljuc: 'narocnikPoslovnaEnota', oznaka: 'Poslovna enota' },
          { kljuc: 'narocnikTelPodjetja', oznaka: 'Tel. št.' },
          { kljuc: 'narocnikFaxPodjetja', oznaka: 'Fax' },
          { kljuc: 'narocnikEPostaPodjetja', oznaka: 'E-pošta' },
          { kljuc: 'narocnikSpletnaPodjetja', oznaka: 'Spletna stran' },
        ],
      },
      {
        naslov: 'Banka',
        pomoc: 'racun-po-eposti',
        polja: [
          {
            kljuc: 'narocnikTRRPodjetja',
            oznaka: 'TRR 1',
            namig: 'SI56 6100 0002 5771 986',
            opis: 'IBAN za plačilo na računih in predračunih (koda UPN QR). Naziv banke lahko dopišete zraven.',
          },
          { kljuc: 'narocnikTRRPodjetja2', oznaka: 'TRR 2' },
          { kljuc: 'narocnikTRRZaNakaziloEslog', oznaka: 'eSLOG – TRR za nakazilo' },
          { kljuc: 'narocnikNazivBankeZaEslog', oznaka: 'eSLOG – naziv banke' },
        ],
      },
    ],
  },
  {
    kljuc: 'splosno',
    naziv: 'Splošno',
    opis: 'Davčne stopnje, zakonodaja na računih in številčenje.',
    sklopi: [
      {
        naslov: 'Davki in zakonodaja',
        polja: [
          { kljuc: 'VelikDDVProcenti', oznaka: 'Splošna stopnja DDV (%)', vrsta: 'stevilo', namig: '22' },
          { kljuc: 'MaliDDVProcenti', oznaka: 'Znižana stopnja DDV (%)', vrsta: 'stevilo', namig: '9,5' },
          { kljuc: 'izpisiOsnovnaZakonodaja', oznaka: 'Osnovna zakonodaja', vrsta: 'dolgo' },
          { kljuc: 'izpisiPrepisZakonodaje', oznaka: 'Prepis zakonodaje', vrsta: 'dolgo' },
        ],
      },
      {
        naslov: 'Računi',
        polja: [
          { kljuc: 'placiloZGotovino', oznaka: 'Gotovinsko plačilo', vrsta: 'da-ne' },
          {
            kljuc: 'predponaStRacuna',
            oznaka: 'Predpona št. računa',
            opis: 'Največ 10 črk, številk in pomišljajev.',
          },
          { kljuc: 'formatDatuma', oznaka: 'Format datuma', namig: 'dd.MM.yyyy' },
        ],
      },
    ],
  },
  {
    kljuc: 'dokumenti',
    naziv: 'Dokumenti',
    opis: 'Glava in noga ter vsebina izpisanih dokumentov.',
    sklopi: [
      {
        naslov: 'Glava in noga',
        pomoc: 'logotip-glava-noga',
        polja: [
          { kljuc: 'pdfHeaderContent', oznaka: 'Glava dokumenta', vrsta: 'dolgo' },
          { kljuc: 'pdfFooterContent', oznaka: 'Noga dokumenta', vrsta: 'dolgo' },
          {
            kljuc: 'velikostPisaveGlavaNoga',
            oznaka: 'Velikost pisave v glavi in nogi',
            vrsta: 'stevilo',
            namig: '10',
          },
          { kljuc: 'spodnjiOdmikNoge', oznaka: 'Spodnji odmik noge dokumenta', vrsta: 'stevilo', namig: '5' },
        ],
      },
      {
        naslov: 'Vsebina',
        polja: [
          { kljuc: 'zacasnaRegistrska', oznaka: 'Začasna registrska' },
          { kljuc: 'veljavnostPreizkusnje', oznaka: 'Veljavnost preizkušnje (dni)', vrsta: 'stevilo', namig: '1' },
          { kljuc: 'garancijaMeseci', oznaka: 'Garancija (meseci)', vrsta: 'stevilo', namig: '0' },
          { kljuc: 'komisijskaPogodba', oznaka: 'Komisijska namesto posredniške pogodbe', vrsta: 'da-ne' },
          { kljuc: 'skrijNaslovPosredniska', oznaka: 'Skrij naslov na posredniški pogodbi', vrsta: 'da-ne' },
          { kljuc: 'skrijPodpisKupca', oznaka: 'Skrij podpis kupca', vrsta: 'da-ne' },
          { kljuc: 'izpisiUporabnikaVozila', oznaka: 'Izpiši uporabnika vozila', vrsta: 'da-ne' },
          { kljuc: 'narocnikizkaziKmNaPdf', oznaka: 'Prikaz kilometrov na PDF', vrsta: 'da-ne' },
        ],
      },
      {
        naslov: 'Podpis prodajalca na računu in predračunu',
        pomoc: 'podpis-na-dokumentih',
        polja: [
          {
            kljuc: 'izpisiIzdajatelja',
            oznaka: 'Izpiši, kdo je izdal dokument',
            vrsta: 'da-ne',
            opis: 'Pod podpisom prodajalca »Izdal: ime in priimek, funkcija« (funkcija pri uporabniku).',
          },
          {
            kljuc: 'podpisNaDokumentih',
            oznaka: 'Natisni podpis izdajatelja',
            vrsta: 'da-ne',
            opis: 'Slika podpisa uporabnika, ki je dokument izdal (naložite jo pri uporabniku).',
          },
        ],
      },
    ],
  },
  {
    kljuc: 'delavnica',
    naziv: 'Delavnica',
    opis: 'E-pošta stranki ob potrditvi termina in ko je vozilo pripravljeno za prevzem.',
    modul: 'delavnica',
    sklopi: [
      {
        naslov: 'E-pošta »potrditev termina«',
        pomoc: 'termini',
        polja: [
          {
            kljuc: 'delavnicaTerminZadeva',
            oznaka: 'Zadeva',
            namig: 'Potrditev termina: {datum} ob {ura}',
            opis: 'Prazno = privzeto besedilo.',
          },
          {
            kljuc: 'delavnicaTerminBesedilo',
            oznaka: 'Besedilo',
            vrsta: 'dolgo',
            namig:
              'Pozdravljeni,\n\npotrjujemo vaš termin v naši delavnici:\n\n{datum} ob {ura}\nVozilo: {vozilo}\n\nČe vam termin ne ustreza, nas pokličite.\n\nLep pozdrav,\n{podjetje}\n{telefon}',
            opis: 'Oznake: {stranka}, {vozilo}, {registrska}, {datum}, {ura}, {opis}, {podjetje}, {telefon}. Brez vozila se vrstica z {vozilo} ali {registrska} izpusti. Prazno = privzeto besedilo.',
          },
        ],
      },
      {
        naslov: 'E-pošta »vozilo je pripravljeno«',
        pomoc: 'delovni-nalogi',
        polja: [
          {
            kljuc: 'delavnicaObvestiloZadeva',
            oznaka: 'Zadeva',
            namig: 'Vaše vozilo {registrska} je pripravljeno',
            opis: 'Prazno = privzeto besedilo.',
          },
          {
            kljuc: 'delavnicaObvestiloBesedilo',
            oznaka: 'Besedilo',
            vrsta: 'dolgo',
            namig:
              'Pozdravljeni,\n\nvaše vozilo {vozilo} je popravljeno in pripravljeno za prevzem.\n\nLep pozdrav,\n{podjetje}\n{telefon}',
            opis: 'Oznake: {stranka}, {vozilo}, {registrska}, {nalog}, {skupaj}, {podjetje}, {telefon}. Prazno = privzeto besedilo.',
          },
        ],
      },
    ],
  },
];

const DA_NE = new Set(
  SKUPINE_NASTAVITEV.flatMap((s) => s.sklopi.flatMap((k) => k.polja))
    .filter((p) => p.vrsta === 'da-ne')
    .map((p) => p.kljuc),
);

/**
 * Vrednost s strežnika za obrazec: stopnja DDV brez »%«, prikaz kilometrov je privzeto vklopljen (kot Swing), druga
 * stikala brez vrednosti (nove nastavitve, stari »null«) so izklopljena – shrani se »false«, ne prazno.
 */
export function zaObrazec(kljuc: string, vrednost: string | undefined): string {
  const v = vrednost ?? '';
  if (kljuc === 'VelikDDVProcenti' || kljuc === 'MaliDDVProcenti') {
    return v.replace('%', '').trim();
  }
  if (kljuc === 'narocnikizkaziKmNaPdf') {
    return v === '' ? 'true' : v;
  }
  if (DA_NE.has(kljuc)) {
    return v === 'true' ? 'true' : 'false';
  }
  if (kljuc === 'spodnjiOdmikNoge') {
    return v.replace('.', ',');
  }
  return v;
}
